import { useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { LearnerCertificateDto, LearningPathDto } from '../general/dtos';
import { useApi } from '../providers/api-provider';
import { useAuth } from '../providers/auth-provider';
import { CertificateDialog } from './dialogs/certificate-dialog';
import { CertificateNameDialog } from './dialogs/certificate-name-dialog';

export function YourCertificates({ learningPath }: { learningPath: LearningPathDto }) {
    const api = useApi();
    const auth = useAuth();
    const identity = auth.getIsAuthenticated() ? auth.getUser()?.sub : undefined;
    const [snapshot, setSnapshot] = useState<{ identity: string; certificates: LearnerCertificateDto[] } | null>(null);
    const [selected, setSelected] = useState<LearnerCertificateDto | null>(null);
    const [error, setError] = useState('');
    const [revision, setRevision] = useState(0);
    const [showAll, setShowAll] = useState(false);

    useEffect(() => {
        setSelected(null);
        setSnapshot(null);
        setShowAll(false);
    }, [identity]);

    useEffect(() => {
        if (!api || !identity) return;
        const controller = new AbortController();
        setError('');
        api.getMyCertificates(controller.signal).then(certificates => {
            if (!controller.signal.aborted) setSnapshot({ identity, certificates });
        }).catch(() => {
            if (!controller.signal.aborted) setError('Could not load your certificates. Please try again.');
        });
        return () => controller.abort();
    }, [api, identity, learningPath, revision]);

    if (!identity) return null;
    const certificates = snapshot?.identity === identity ? snapshot.certificates : [];

    return <>
        <section className="learning-summary-card learning-achievements" aria-label="Your achievements">
            <h4><i className="pi pi-trophy" aria-hidden="true" />Your achievements</h4>
            {error ? <div role="alert">{error} <Button label="Retry certificates" text onClick={() => setRevision(value => value + 1)} /></div>
                : snapshot?.identity !== identity ? <p className="text-color-secondary text-sm" role="status">Loading your certificates…</p>
                : !certificates.length ? <p className="learning-achievements-empty">Certificates you earn will have a home here. Certificates are available for courses that offer them.</p>
                : <><ul className="learning-achievements-list">
                    {(showAll ? certificates : certificates.slice(0, 3)).map(certificate => <li key={certificate.key} className="learning-achievement">
                        <div className="learning-certificate-preview" aria-hidden="true"><i className="pi pi-verified" />
                            {certificate.name_confirmed !== false && <img src={api?.getCertificateImageUrl(certificate.key)} alt="" loading="lazy"
                                onError={event => { event.currentTarget.style.display = 'none'; }} />}
                        </div>
                        <div className="learning-achievement-details"><strong>{certificate.course_name}</strong>
                            <span>{certificate.name_confirmed === false ? 'Confirm your name to finish your certificate' : 'Course certificate'}</span>
                            <Button label="Open certificate" text icon="pi pi-eye" onClick={() => setSelected(certificate)} />
                        </div>
                    </li>)}
                </ul>
                {certificates.length > 3 && <Button label={showAll ? 'Show fewer certificates' : `Show all certificates (${certificates.length})`} text
                    className="learning-achievements-more" aria-expanded={showAll} onClick={() => setShowAll(value => !value)} />}</>}
        </section>
        {selected?.name_confirmed === false ? <CertificateNameDialog key={`${identity}:${selected.key}`}
            certificate={{ key: selected.key, course_name: selected.course_name, default_name: selected.default_name ?? '' }}
            onLater={() => setSelected(null)} onConfirmed={() => {
                setSnapshot(current => current?.identity === identity ? { ...current,
                    certificates: current.certificates.map(certificate => certificate.key === selected.key
                        ? { ...certificate, name_confirmed: true } : certificate) } : current);
                setSelected({ ...selected, name_confirmed: true });
            }} /> : selected && <CertificateDialog key={`${identity}:${selected.key}`} certificate={selected} onClose={() => setSelected(null)} />}
    </>;
}
